import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { yieldToMainThread } from '@theme/utilities';
import { isBundleEligibleProduct } from '@theme/na-bundle-discount';
import {
    readCardTags,
    readRoomDiscountContext,
    renderRoomInlinePrice,
    resolveRoomItemDiscount,
} from '@theme/na-room-pricing';
import { swapRoomProduct } from '@theme/na-room-product-swap';

/**
 * @typedef {Object} DropdownVariantMetafields
 * @property {number|null} metafield_discount
 * @property {number|null} metafield_recommended_discount
 * @property {string|null} [delivery_tag]
 * @property {string|null} [custom_swatches]
 * @property {string|null} [swatch_image_url]
 * @property {string|null} [variant_image_url]
 */

/** @extends {Component<Record<string, never>>} */
export class RoomDropdownComponent extends Component {
    /** @type {AbortController|null} */
    #abortController = null;

    /** @type {AbortController|null} */
    #documentAbortController = null;

    /** @type {AbortController|null} */
    #fetchAbortController = null;

    connectedCallback() {
        super.connectedCallback();
        this.#abortController = new AbortController();
        this.#setupEventListeners();
        this.#populateDiscountBadges();
    }

    updatedCallback() {
        super.updatedCallback();
        this.#abortController?.abort();
        this.#abortController = new AbortController();
        this.#setupEventListeners();
        this.#populateDiscountBadges();
        if (this.#isOpen()) this.#attachDocumentListeners();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController?.abort();
        this.#detachDocumentListeners();
        this.#fetchAbortController?.abort();
        removeTrapFocus();
        document.body.style.overflow = '';
    }

    #setupEventListeners() {
        const signal = this.#abortController?.signal;

        this.querySelector('.room-info__dropdown-trigger')?.addEventListener('click', this.#toggleDropdown, { signal });
        this.querySelector('.room-info__dropdown-backdrop')?.addEventListener('click', this.#closeDropdown, { signal });
        this.querySelector('.room-info__dropdown-close')?.addEventListener('click', this.#closeDropdown, { signal });

        this.querySelectorAll('.room-info__dropdown-item').forEach((option) => {
            option.addEventListener('click', this.#handleOptionSelect, { signal });
        });

        this.querySelector('.room-info__dropdown-list')?.addEventListener('keydown', this.#handleListKeydown, {
            signal,
        });

        this.querySelectorAll('.room-info__dropdown-swatches').forEach((swatches) => {
            swatches.addEventListener('click', (event) => event.stopPropagation(), { signal });
            swatches.addEventListener('change', this.#handleSwatchChange, { signal });
        });
    }

    #attachDocumentListeners() {
        this.#detachDocumentListeners();
        this.#documentAbortController = new AbortController();
        const { signal } = this.#documentAbortController;

        document.addEventListener(
            'keydown',
            (event) => {
                if (event.key === 'Escape' && this.#isOpen()) {
                    event.preventDefault();
                    this.#closeDropdown();
                }
            },
            { signal }
        );

        document.addEventListener(
            'click',
            (event) => {
                if (this.#isOpen() && !this.contains(/** @type {Node} */ (event.target))) {
                    this.#closeDropdown();
                }
            },
            { signal }
        );
    }

    #detachDocumentListeners() {
        this.#documentAbortController?.abort();
        this.#documentAbortController = null;
    }

    #populateDiscountBadges() {
        const roomInfoEl = this.closest('room-info-component');
        const context = readRoomDiscountContext(roomInfoEl instanceof HTMLElement ? roomInfoEl : null);

        this.querySelectorAll('.room-info__dropdown-item').forEach((item) => {
            if (!(item instanceof HTMLElement)) return;

            const priceWrap = item.querySelector('.na-product-card-inline__price');
            const priceEl = item.querySelector('.price');
            if (!(priceWrap instanceof HTMLElement) || !(priceEl instanceof HTMLElement)) return;

            const productId = priceEl.dataset.productId || item.dataset.productId;
            const variantId = parseInt(priceEl.dataset.variantId ?? '', 10);
            if (!productId || Number.isNaN(variantId)) return;

            /** @type {{
             *   metafield_discount?: number | null,
             *   metafield_recommended_discount?: number | null,
             *   variants?: Record<string, {
             *     price?: number,
             *     compare_at_price?: number | null,
             *     metafield_discount?: number | null,
             *     metafield_recommended_discount?: number | null,
             *   }>
             * }} */
            let metafields = {};
            const metafieldsScript = item.querySelector('script.room-info__dropdown-metafields');
            if (metafieldsScript?.textContent) {
                try {
                    metafields = JSON.parse(metafieldsScript.textContent);
                } catch {
                    metafields = {};
                }
            }

            const variantMeta = metafields.variants?.[String(variantId)] || {};
            const price = Number(variantMeta.price);
            if (!Number.isFinite(price) || price <= 0) return;

            const resolved = resolveRoomItemDiscount({
                price,
                compareAt: variantMeta.compare_at_price,
                isEligible: isBundleEligibleProduct(
                    {
                        price: 0,
                        product_type: item.dataset.productType || '',
                        tags: readCardTags(item),
                        is_free: false,
                    },
                    context.bundleSettings
                ),
                useGlobalBundle: context.useGlobalBundle,
                bundlePercent: context.bundlePercent,
                sectionPercent: context.sectionPercent,
                sectionDollars: context.sectionDollars,
                variantMetaDiscount: variantMeta.metafield_discount,
                productMetaDiscount: metafields.metafield_discount,
                variantRecDiscount: variantMeta.metafield_recommended_discount,
                productRecDiscount: metafields.metafield_recommended_discount,
            });

            renderRoomInlinePrice(priceWrap, {
                productId,
                variantId,
                finalPrice: resolved.finalPrice,
                originalPrice: resolved.originalPrice,
                badgeText: resolved.badgeText,
            });
        });
    }

    /** @param {Event} event */
    #handleSwatchChange = (event) => {
        event.stopPropagation();

        const input = event.target;
        if (!(input instanceof HTMLInputElement) || !input.checked) return;

        const item = input.closest('.room-info__dropdown-item');
        if (!(item instanceof HTMLElement)) return;

        const label = input.labels?.[0] ?? item.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (!(label instanceof HTMLElement)) return;

        const imgUrl = label.dataset.variantImage;
        if (imgUrl) {
            const img = item.querySelector('.na-product-card-inline__img img');
            if (img instanceof HTMLImageElement) img.src = imgUrl;
        }

        const variantId = label.dataset.variantId;
        if (variantId) item.dataset.variantId = variantId;

        const variantActive = item.querySelector('.na-product-card-inline__variant-active p');
        if (variantActive) variantActive.textContent = input.value;
    };

    #isOpen() {
        const menu = this.querySelector('.room-info__dropdown-menu');
        return menu?.getAttribute('aria-expanded') === 'true';
    }

    /** @returns {HTMLElement[]} */
    #optionButtons() {
        return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="option"]')]);
    }

    /** @param {KeyboardEvent} event */
    #handleListKeydown = (event) => {
        if (!this.#isOpen()) return;
        if (
            event.target instanceof HTMLElement &&
            event.target.closest('input, select, textarea, [contenteditable="true"]')
        ) {
            return;
        }

        const options = this.#optionButtons();
        if (!options.length) return;

        const currentIndex = options.findIndex((option) => option === document.activeElement);
        const lastIndex = options.length - 1;

        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
            event.preventDefault();
            options[(currentIndex + 1 + options.length) % options.length]?.focus();
            return;
        }

        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
            event.preventDefault();
            options[(currentIndex - 1 + options.length) % options.length]?.focus();
            return;
        }

        if (event.key === 'Home') {
            event.preventDefault();
            options[0]?.focus();
            return;
        }

        if (event.key === 'End') {
            event.preventDefault();
            options[lastIndex]?.focus();
            return;
        }

        if ((event.key === 'Enter' || event.key === ' ') && event.target instanceof HTMLElement) {
            const option = event.target.closest('[role="option"]');
            if (option instanceof HTMLElement) {
                event.preventDefault();
                option.click();
            }
        }
    };

    #toggleDropdown = () => {
        this.#setExpanded(!this.#isOpen());
    };

    #closeDropdown = () => {
        this.#setExpanded(false);
    };

    /** @param {boolean} expanded */
    #setExpanded(expanded) {
        const trigger = this.querySelector('.room-info__dropdown-trigger');
        const popup = this.querySelector('.room-info__dropdown-popup');
        trigger?.setAttribute('aria-expanded', String(expanded));
        this.querySelector('.room-info__dropdown-menu')?.setAttribute('aria-expanded', String(expanded));

        if (expanded) {
            this.#attachDocumentListeners();
            if (window.innerWidth >= 1000) document.body.style.overflow = 'hidden';
            if (popup instanceof HTMLElement) {
                trapFocus(popup);
                const selected =
                    this.querySelector('[role="option"][aria-selected="true"]') || this.#optionButtons()[0];
                if (selected instanceof HTMLElement) selected.focus();
            }
            return;
        }

        removeTrapFocus();
        this.#detachDocumentListeners();
        document.body.style.overflow = '';
        if (trigger instanceof HTMLElement) trigger.focus();
    }

    /** @param {Event} event */
    #handleOptionSelect = async (event) => {
        const option = /** @type {HTMLElement} */ (
            event.currentTarget instanceof HTMLElement ? event.currentTarget.closest('.room-info__dropdown-item') : null
        );
        const productHandle = option?.dataset.productHandle;
        const productId = option?.dataset.productId;
        if (!option || !productHandle || !productId) return;

        this.#closeDropdown();
        await yieldToMainThread();

        this.querySelectorAll('.room-info__dropdown-item').forEach((opt) => {
            opt.classList.remove('room-info__dropdown-item--selected');
            const select = opt.querySelector('[role="option"]');
            if (select instanceof HTMLElement) {
                select.setAttribute('aria-selected', 'false');
                select.tabIndex = -1;
            }
        });
        option.classList.add('room-info__dropdown-item--selected');
        const selectedButton = option.querySelector('[role="option"]');
        if (selectedButton instanceof HTMLElement) {
            selectedButton.setAttribute('aria-selected', 'true');
            selectedButton.tabIndex = 0;
        }

        const titleElement = this.querySelector('.room-info__dropdown-title');
        const previousTitle = titleElement?.textContent ?? '';
        if (titleElement) titleElement.textContent = option.dataset.productTitle || '';

        this.dispatchEvent(
            new CustomEvent('room:product-change', {
                detail: { productHandle, productId },
                bubbles: true,
            })
        );

        /** @type {{ metafield_discount: number|null, metafield_recommended_discount: number|null, description: string|null, features_text: string|null, features_list: Array<{text: string, image_url: string|null}>, variants: Record<string, DropdownVariantMetafields> }} */
        const metafields = {
            metafield_discount: null,
            metafield_recommended_discount: null,
            description: null,
            features_text: null,
            features_list: [],
            variants: {},
        };
        const metafieldsScript = option.querySelector('script.room-info__dropdown-metafields');
        if (metafieldsScript) {
            try {
                Object.assign(metafields, JSON.parse(metafieldsScript.textContent || '{}'));
            } catch (e) {
                console.error('Failed to parse dropdown metafields', e);
            }
        }

        const checkedInput = option.querySelector('.na-form__input--color:checked');
        const checkedLabel = checkedInput?.closest('.na-form__field')?.querySelector('.na-form__label--color');
        const preferredVariantId =
            option.dataset.variantId ||
            (checkedLabel instanceof HTMLElement ? checkedLabel.dataset.variantId : '') ||
            '';

        const productCard = this.closest('.room-info__product');
        if (!(productCard instanceof HTMLElement)) return;

        this.#fetchAbortController?.abort();
        this.#fetchAbortController = new AbortController();

        try {
            const result = await swapRoomProduct({
                productCard,
                productHandle,
                productId,
                preferredVariantId,
                metafields,
                signal: this.#fetchAbortController.signal,
            });
            if (!result?.variant) return;
            this.#updateDropdownOptionDisplay(
                option,
                /** @type {{ id: number, title: string, featured_image: string, variants: Array<Record<string, any>>, tags?: string[], product_type?: string }} */ (
                    result.product
                ),
                result.variant,
                metafields.variants,
                productCard
            );
        } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') return;
            console.error('Error loading product:', error);
            if (titleElement) titleElement.textContent = previousTitle;
        }
    };

    /**
     * @param {HTMLElement} option
     * @param {{ id: number, title: string, featured_image: string, variants: Array<Record<string, any>>, tags?: string[], product_type?: string }} product
     * @param {Record<string, any>} targetVariant
     * @param {Record<string, DropdownVariantMetafields>} variantMetafields
     * @param {HTMLElement} productCard
     */
    #updateDropdownOptionDisplay(option, product, targetVariant, variantMetafields, productCard) {
        const meta = /** @type {DropdownVariantMetafields} */ (variantMetafields[String(targetVariant.id)] || {});
        const imageUrl = meta.variant_image_url || product.featured_image;

        if (imageUrl) {
            const img = option.querySelector('.na-product-card-inline__img img');
            if (img instanceof HTMLImageElement) {
                img.src = imageUrl;
                img.alt = product.title;
            }
        }

        const variantActive = option.querySelector('.na-product-card-inline__variant-active p');
        if (variantActive) {
            variantActive.textContent =
                targetVariant.title && targetVariant.title !== 'Default Title' ? targetVariant.title : '';
        }

        option.querySelectorAll('.room-info__dropdown-swatches input').forEach((input) => {
            if (!(input instanceof HTMLInputElement)) return;
            const label = input.closest('.na-form__field')?.querySelector('.na-form__label--color');
            input.checked = label instanceof HTMLElement && label.dataset.variantId === String(targetVariant.id);
        });

        const roomInfoEl = productCard.closest('room-info-component');
        const context = readRoomDiscountContext(roomInfoEl instanceof HTMLElement ? roomInfoEl : null);
        const isFree = productCard.dataset.isFree === 'true';

        /** @type {{ metafield_discount?: number | null, metafield_recommended_discount?: number | null }} */
        let productMetafields = {};
        const metafieldsScript = option.querySelector('script.room-info__dropdown-metafields');
        if (metafieldsScript?.textContent) {
            try {
                productMetafields = JSON.parse(metafieldsScript.textContent);
            } catch {
                productMetafields = {};
            }
        }

        const tags = product.tags?.length ? product.tags : readCardTags(option);
        const productType = product.product_type || option.dataset.productType || productCard.dataset.productType || '';

        const resolved = resolveRoomItemDiscount({
            price: targetVariant.price,
            compareAt: targetVariant.compare_at_price,
            isFree,
            isEligible: isBundleEligibleProduct(
                { price: 0, product_type: productType, tags, is_free: isFree },
                context.bundleSettings
            ),
            useGlobalBundle: context.useGlobalBundle,
            bundlePercent: context.bundlePercent,
            sectionPercent: context.sectionPercent,
            sectionDollars: context.sectionDollars,
            variantMetaDiscount: targetVariant.metafield_discount ?? meta.metafield_discount,
            productMetaDiscount: productMetafields.metafield_discount,
            variantRecDiscount: targetVariant.metafield_recommended_discount ?? meta.metafield_recommended_discount,
            productRecDiscount: productMetafields.metafield_recommended_discount,
        });

        const priceWrap = option.querySelector('.na-product-card-inline__price');
        if (!(priceWrap instanceof HTMLElement)) return;

        renderRoomInlinePrice(priceWrap, {
            productId: product.id,
            variantId: targetVariant.id,
            finalPrice: resolved.finalPrice,
            originalPrice: resolved.originalPrice,
            badgeText: resolved.badgeText,
            isFree,
        });
    }
}

if (!customElements.get('room-dropdown-component')) {
    customElements.define('room-dropdown-component', RoomDropdownComponent);
}
